import * as THREE from "three";

export class ThreeEquirectRenderer {
  constructor(canvas, video) {
    this.canvas = canvas;
    this.video = video;
    this.perf = { 
      maxFps: 60, 
      useRVFC: true, 
      qualityBias: 1, 
      dprMin: 0.5, 
      dprMax: 3.0, 
      quality: "high" 
    };
    this.lastResize = 0;
    this.lastFov = 80;
    this._isRendering = false;
    this.resizeTimeout = null;

    // Initialize Three.js scene
    this.initThreeJS();
    
    // Setup resize observer with proper error handling
    this.setupResizeObserver();
  }

  initThreeJS() {
    // Create renderer
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: false,
      powerPreference: "high-performance"
    });
    
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(this.canvas.clientWidth, this.canvas.clientHeight);
    this.renderer.setClearColor(0x000000, 1.0);
    if (this.renderer.outputColorSpace !== undefined) {
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    }
    
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping || THREE.LinearToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    // Create scene
    this.scene = new THREE.Scene();

    // Create camera with adjusted near/far planes for better 360° viewing
    this.camera = new THREE.PerspectiveCamera(
      75, // fov
      this.canvas.clientWidth / this.canvas.clientHeight, // aspect
      0.01, // near - closer to avoid z-fighting
      200 // far - closer to match sphere radius
    );

    // Create sphere geometry for 360° video - smaller radius for better performance and stability
    const geometry = new THREE.SphereGeometry(100, 60, 40);
    // geometry.scale(1, 1, 1); // squish vertically for 2:1 video
    // Create video texture
    
    this.videoTexture = new THREE.VideoTexture(this.video);
    this.videoTexture.minFilter = THREE.LinearFilter;
    this.videoTexture.magFilter = THREE.LinearFilter;
    this.videoTexture.wrapS = THREE.ClampToEdgeWrapping;
    this.videoTexture.wrapT = THREE.ClampToEdgeWrapping;
    this.videoTexture.flipY = true; // Important for 360° video
    
    if (this.videoTexture.colorSpace !== undefined) {
      this.videoTexture.colorSpace = THREE.SRGBColorSpace;
    }
    this.videoTexture.generateMipmaps = false; // Disable mipmaps for video textures
    // Create material
    const material = new THREE.MeshBasicMaterial({
      map: this.videoTexture,
      side: THREE.BackSide, // Render inside of sphere
      toneMapped: true, // Enable tone mapping
      transparent: false,
      opacity: 1.0
    });
    // const material = new THREE.ShaderMaterial({
    //   uniforms: {
    //     uVideo: { value: this.videoTexture },
    //     uTime: { value: 0 },
    //     uWarp: { value: 0.8 }, // fisheye intensity
    //     uFallbackColor: { value: new THREE.Vector3(0.2, 0.2, 0.2) } // fallback gray
    //   },
    //   vertexShader: `
    //     varying vec2 vUv;
    //     void main() {
    //       vUv = uv;
    //       vec3 pos = position;
    //       pos.y *= 1.0; // tweak for vertical stretch
    //       gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
    //     }
    //   `,
    //   fragmentShader: `
    //     uniform sampler2D uVideo;
    //     uniform float uWarp;
    //     uniform vec3 uFallbackColor;
    //     varying vec2 vUv;
    
    //     void main() {
    //       vec2 centered = vUv - 0.5;
    
    //       // fisheye / warp
    //       float r = length(centered);
    //       float theta = atan(centered.y, centered.x);
    //       r = pow(r, uWarp);
    //       vec2 warped = vec2(cos(theta), sin(theta)) * r + 0.5;
    
    //       vec4 videoColor = texture2D(uVideo, warped);
    
    //       // If video pixel is almost black, replace with fallback
    //       float threshold = 0.01; // adjust if needed
    //       if (videoColor.r < threshold && videoColor.g < threshold && videoColor.b < threshold) {
    //         videoColor = vec4(uFallbackColor, 1.0);
    //       }
    
    //       gl_FragColor = videoColor;
    //     }
    //   `,
    //   side: THREE.BackSide
    // });
    // Create mesh
    this.sphere = new THREE.Mesh(geometry, material);
    this.scene.add(this.sphere);

    // Position camera at center
    this.camera.position.set(0, 0, 0);

    // Create controls for camera movement (optional)
    this.setupControls();
  }

  setupResizeObserver() {
    // Suppress ResizeObserver errors
    const originalError = console.error;
    console.error = (...args) => {
      if (args[0] && args[0].includes && args[0].includes('ResizeObserver loop completed with undelivered notifications')) {
        return; // Suppress this specific error
      }
      originalError.apply(console, args);
    };

    // Setup ResizeObserver with debounced resize
    this.resizeObserver = new ResizeObserver((entries) => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      
      this.resizeTimeout = setTimeout(() => {
        const entry = entries[0];
        if (entry && entry.contentRect) {
          const { width, height } = entry.contentRect;
          if (width > 0 && height > 0) {
            this.handleResize();
          }
        }
      }, 100); // Debounce resize by 100ms
    });

    this.resizeObserver.observe(this.canvas);
  }

  setupControls() {
    // Disable built-in mouse controls - usePanZoom hook handles mouse interaction
    // This prevents conflicts between the renderer's controls and the hook's controls
    this.mouseX = 0;
    this.mouseY = 0;
    this.targetRotationX = 0;
    this.targetRotationY = 0;
    this.rotationSpeed = 0.01;

    // No event listeners added - mouse controls are handled by usePanZoom hook
    // This ensures the camera position is maintained after mouse movement stops
  }

  setPerf(opts) {
    this.perf = { ...this.perf, ...opts };
    
    // Apply quality presets
    if (opts.quality) {
      const presets = {
        low: { 
          qualityBias: 0.7, 
          dprMin: 0.3, 
          dprMax: 1.5, 
          maxFps: 30 
        },
        medium: { 
          qualityBias: 1.0, 
          dprMin: 0.5, 
          dprMax: 2.0, 
          maxFps: 45 
        },
        high: { 
          qualityBias: 1.2, 
          dprMin: 0.7, 
          dprMax: 2.5, 
          maxFps: 60 
        },
        ultra: { 
          qualityBias: 1.5, 
          dprMin: 1.0, 
          dprMax: 3.0, 
          maxFps: 60 
        }
      };
      const preset = presets[opts.quality];
      if (preset) {
        this.perf = { ...this.perf, ...preset };
      }
    }

    // Update renderer settings based on performance
    this.renderer.setPixelRatio(
      Math.min(window.devicePixelRatio * this.perf.qualityBias, this.perf.dprMax)
    );
  }

  handleResize() {
    const now = performance.now();
    if (now - this.lastResize < 200) return; // Debounce resize
    this.lastResize = now;
    
    // Prevent resize during render
    if (this._isRendering) return;
    
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    
    // Only resize if dimensions actually changed
    if (width === this.lastWidth && height === this.lastHeight) {
      return;
    }
    
    this.lastWidth = width;
    this.lastHeight = height;
    
    // Update camera aspect ratio
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    
    // Update renderer size
    this.renderer.setSize(width, height);
  }

  resize(fovDeg) {
    const now = performance.now();
    if (now - this.lastResize < 200) return; // Debounce resize
    this.lastResize = now;
    
    // Prevent resize during render
    if (this._isRendering) return;
    
    // Get video dimensions for quality calculation
    const videoWidth = this.video.videoWidth || 1920;
    const videoHeight = this.video.videoHeight || 1080;
    
    // Calculate base DPR with quality bias
    const baseDpr = (window.devicePixelRatio || 1) * this.perf.qualityBias;
    
    // FOV scaling
    const fovScale = Math.pow(80 / Math.max(30, Math.min(120, fovDeg)), 0.3);
    
    // Consider video resolution for optimal scaling
    const resolutionScale = Math.min(2.0, Math.max(0.5, Math.sqrt((videoWidth * videoHeight) / (1920 * 1080))));
    
    // Calculate target DPR
    const targetDpr = Math.max(
      this.perf.dprMin, 
      Math.min(this.perf.dprMax, baseDpr * fovScale * resolutionScale)
    );
    
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    
    // Update camera aspect ratio and FOV
    this.camera.aspect = width / height;
    this.camera.fov = fovDeg;
    this.camera.updateProjectionMatrix();
    
    // Update renderer size
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(targetDpr);
  }

  render(yawRad, pitchRad, fovRad) {
    this._isRendering = true;
    
    // Convert radians to degrees
    const fovDeg = fovRad * 180 / Math.PI;
    this.lastFov = fovDeg;
    
    // Update camera rotation based on yaw and pitch from usePanZoom hook
    // Standard 360° camera behavior: YXZ rotation order for proper pitch/yaw
    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.y = yawRad;  // Yaw (left/right rotation)
    this.camera.rotation.x = -pitchRad;  // Pitch (up/down rotation) - inverted for standard behavior
    
    // No additional mouse/touch rotation applied - usePanZoom hook handles all mouse interaction
    // This ensures the camera position is maintained exactly as set by the hook
    
    // Clamp pitch to prevent over-rotation (standard 360° camera limits)
    this.camera.rotation.x = Math.max(-Math.PI/2, Math.min(Math.PI/2, this.camera.rotation.x));
    
    // Update video texture if video is ready
    if (this.video.readyState >= this.video.HAVE_CURRENT_DATA && this.video.videoWidth > 0) {
      this.videoTexture.needsUpdate = true;
      
      // Debug: Log video dimensions for 360 streams
      if (this.video.videoWidth === 3840 && this.video.videoHeight === 1920) {
        console.log('360 video detected:', this.video.videoWidth + 'x' + this.video.videoHeight);
      }
    } else {
      // Debug: Log video state
      console.log('Video not ready:', {
        readyState: this.video.readyState,
        videoWidth: this.video.videoWidth,
        videoHeight: this.video.videoHeight,
        currentTime: this.video.currentTime
      });
    }
    
    // Resize if needed
    this.resize(fovDeg);
    
    // Render the scene
    this.renderer.render(this.scene, this.camera);
    
    this._isRendering = false;
  }

  // Method to update camera rotation programmatically
  setRotation(yawRad, pitchRad) {
    this.camera.rotation.y = yawRad;
    this.camera.rotation.x = pitchRad;
  }

  // Method to get current rotation
  getRotation() {
    return {
      yaw: this.camera.rotation.y,
      pitch: this.camera.rotation.x
    };
  }

  // Method to reset rotation
  resetRotation() {
    this.camera.rotation.set(0, 0, 0);
    this.targetRotationX = 0;
    this.targetRotationY = 0;
  }

  // Method to set rotation speed
  setRotationSpeed(speed) {
    this.rotationSpeed = speed;
  }

  // Method to enable/disable controls
  setControlsEnabled(enabled) {
    if (enabled) {
      this.setupControls();
    } else {
      this.canvas.removeEventListener('mousemove', this.onMouseMove);
      this.canvas.removeEventListener('touchmove', this.onTouchMove);
    }
  }

  // Cleanup method
  dispose() {
    // Cleanup ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    
    // Clear resize timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }
    
    if (this.videoTexture) {
      this.videoTexture.dispose();
    }
    if (this.sphere && this.sphere.material) {
      this.sphere.material.dispose();
    }
    if (this.sphere && this.sphere.geometry) {
      this.sphere.geometry.dispose();
    }
    if (this.renderer) {
      this.renderer.dispose();
    }
  }
}
